import { getBookNameDescriptor } from "@/utils/bible-books-data";
import { i18n } from "@lingui/core";

export interface VerseRef {
  book: number;
  chapter: number;
  verse: number;
}

function formatChapterRanges(verseNums: number[]): string {
  const sorted = verseNums.toSorted((a, b) => a - b);
  const ranges: string[] = [];
  const [firstVal] = sorted;
  if (firstVal === undefined) {
    return "";
  }
  let start = firstVal;
  let prev = firstVal;

  const pushRange = () => {
    ranges.push(start === prev ? String(start) : `${start}-${prev}`);
  };

  for (const curr of sorted.slice(1)) {
    if (curr !== prev + 1) {
      pushRange();
      start = curr;
    }
    prev = curr;
  }
  pushRange();

  return ranges.join(", ");
}

/**
 * Human-readable reference for a set of verses, collapsing consecutive
 * verses into ranges and grouping by book and chapter:
 * `Genesis 1:1-3, 5; 2:1; John 3:16`.
 * Returns `fallback` when the list is empty.
 */
export function formatVerseReference(verses: VerseRef[], fallback = ""): string {
  if (verses.length === 0) {
    return fallback;
  }

  // Group by book, then by chapter (notes can link verses across books)
  const byBook = new Map<number, Map<number, number[]>>();
  for (const v of verses) {
    let chapters = byBook.get(v.book);
    if (!chapters) {
      chapters = new Map();
      byBook.set(v.book, chapters);
    }
    let list = chapters.get(v.chapter);
    if (!list) {
      list = [];
      chapters.set(v.chapter, list);
    }
    list.push(v.verse);
  }

  const bookParts = [...byBook.entries()]
    .toSorted(([a], [b]) => a - b)
    .map(([book, chapters]) => {
      const chapterParts = [...chapters.entries()]
        .toSorted(([a], [b]) => a - b)
        .map(([chapter, verseNums]) => `${chapter}:${formatChapterRanges(verseNums)}`);
      return `${i18n._(getBookNameDescriptor(book))} ${chapterParts.join("; ")}`;
    });

  return bookParts.join("; ");
}
